import * as React from 'react';
import { styled } from '@mui/material/styles';
import * as PhotoStatus from '../../../../../../../shared/PhotoStatus';
import { DraggableImageCard } from '../../imageDeliveryReordering/view/DraggableImageCard';
import ImagePreviewDialog from '../../imagePreview/view/ImagePreviewDialog';
import { IImageThumbnail } from '../../../Entities';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useImageOrdering } from '../../imageDeliveryReordering/state/use-image-ordering';
import { useImagePreviewLoading } from '../../imagePreview/state/use-image-preview-loading';
import { usePreviewFilename } from '../../imagePreview/state/use-preview-filename';
import { ICost } from '../../../../../../../shared/localization/currency';
import { ConfirmExtraImagePurchaseDialog } from './ConfirmExtraImagePurchaseDialog';
import { useConfirmExtraImagePurchase } from '../state/use-confirm-extra-image-purchase';
import { canPlacePinOnImage } from '../../imagePin/state/utils';

let ImageGrid = styled('article')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 260px)',
    justifyContent: 'center',
    gap: '36px',
    padding: '16px',
    [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        padding: '8px'
    }
}));

type ImagesSectionProps = {
    user: AuthenticatedUser;
    images: IImageThumbnail[];
    orderId: number;
    isSelecting: boolean;
    selectedImageIds: Set<number>;
    isSelectionFull: boolean;
    onToggleSelect: (id: number) => void;
    getEffectiveCategory: (image: IImageThumbnail) => number;
    onToggleCategory: (imageId: number) => void;
    deliveredCount: number;
    orderedCount: number;
    photoStatus: number;
    isStitching: boolean;
    selectedStitchHeight: number | null;
    isPinning: boolean;
    allowPinning: boolean;
    onPinImage: (imageId: number, onCancel?: () => void) => void;
    extraImageCost: ICost | null;
    isApprovalInProgress: boolean;
};

export function CustomerImageList({
    user,
    images,
    orderId,
    isSelecting,
    selectedImageIds,
    isSelectionFull,
    onToggleSelect,
    getEffectiveCategory,
    onToggleCategory,
    deliveredCount,
    orderedCount,
    photoStatus,
    isStitching,
    selectedStitchHeight,
    isPinning,
    allowPinning,
    onPinImage,
    extraImageCost,
    isApprovalInProgress
}: ImagesSectionProps) {
    let {
        orderedImages,
        moveImage,
        handleDragEnd,
        updateImageFilename,
        isImageDraggable,
        isDragActive
    } = useImageOrdering({ images, orderId, getEffectiveCategory });
    let {
        previewImage,
        setPreviewImage,
        updateCachedFilename,
        invalidatePreview,
        prefetchPreview,
        openPreview,
        closePreview
    } = useImagePreviewLoading({ orderId, images: orderedImages, photoStatus });
    let { savePreviewFilename } = usePreviewFilename({
        previewImage,
        setPreviewImage,
        updateCachedFilename,
        updateImageFilename
    });

    let {
        handleToggleCategory,
        handleCancelExtraImagePurchase,
        handleConfirmExtraImagePurchase,
        showConfirmExtraImagePurchaseDialog
    } = useConfirmExtraImagePurchase({
        orderId,
        user,
        onPurchaseSuccess: invalidatePreview,
        onCategoryOverride: onToggleCategory
    });

    let previewCategory = previewImage
        ? getEffectiveCategory(
              orderedImages.find((img) => img.id === previewImage.id) || (previewImage as any)
          )
        : 0;

    let previewThumbnail = previewImage ? orderedImages.find((img) => img.id === previewImage.id) : undefined;

    return (
        <>
            <ImageGrid
                style={{
                    opacity: isApprovalInProgress ? 0.5 : 1,
                    pointerEvents: isApprovalInProgress ? 'none' : 'auto',
                    transition: 'opacity 0.3s ease'
                }}
            >
                {orderedImages.map((image, index) => {
                    return (
                        <DraggableImageCard
                            key={image.id}
                            image={image}
                            index={index}
                            moveImage={moveImage}
                            onDragEnd={handleDragEnd}
                            onClick={function handleClickImage() {
                                if (isApprovalInProgress) {
                                    return;
                                }
                                if (isSelecting) {
                                    onToggleSelect(image.id);
                                } else if (isPinning) {
                                    if (canPlacePinOnImage(getEffectiveCategory(image), image)) {
                                        onPinImage(image.id);
                                    }
                                } else {
                                    openPreview(image.id);
                                }
                            }}
                            onHover={function handleHoverImage() {
                                prefetchPreview(image.id);
                            }}
                            isSelecting={isSelecting}
                            isSelected={selectedImageIds.has(image.id)}
                            isSelectionFull={isSelectionFull}
                            effectiveCategory={getEffectiveCategory(image)}
                            onToggleCategory={() => {
                                if (image.isCustomerApproved) {
                                    return;
                                }
                                if (photoStatus >= PhotoStatus.CustomerApproved) {
                                    handleToggleCategory(image.id);
                                } else {
                                    onToggleCategory(image.id);
                                }
                            }}
                            deliveredCount={deliveredCount}
                            orderedCount={orderedCount}
                            isDraggable={isImageDraggable(image)}
                            isDragActive={isDragActive}
                            photoStatus={photoStatus}
                            isStitching={isStitching}
                            selectedStitchHeight={selectedStitchHeight}
                            isPinning={isPinning}
                        />
                    );
                })}
            </ImageGrid>
            {photoStatus >= PhotoStatus.CustomerApproved ? (
                <ConfirmExtraImagePurchaseDialog
                    open={showConfirmExtraImagePurchaseDialog}
                    extraImageCost={extraImageCost}
                    onConfirm={handleConfirmExtraImagePurchase}
                    onCancel={handleCancelExtraImagePurchase}
                />
            ) : null}
            <ImagePreviewDialog
                user={user}
                open={previewImage !== null}
                onClose={closePreview}
                src={previewImage ? previewImage.src : ''}
                filename={previewImage ? previewImage.filename : ''}
                category={previewCategory}
                idList={orderedImages.map((image) => image.id)}
                currentImageId={previewImage ? previewImage.id : 0}
                onNavigate={(newImageId) => {
                    openPreview(newImageId);
                }}
                orderId={orderId}
                onFilenameSave={savePreviewFilename}
                onPrefetch={prefetchPreview}
                photoStatus={photoStatus}
                canPlacePin={
                    allowPinning &&
                    previewThumbnail !== undefined &&
                    canPlacePinOnImage(previewCategory, previewThumbnail)
                }
                onPlacePin={function handlePlacePinFromPreview() {
                    if (!previewImage) {
                        return;
                    }
                    let imageId = previewImage.id;
                    closePreview();
                    onPinImage(imageId, function reopenPreview() {
                        openPreview(imageId);
                    });
                }}
            />
        </>
    );
}
